package com.upcominds.expedite.features.leaders

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.upcominds.expedite.data.leaders.LeadersRepository
import com.upcominds.expedite.data.mobilelogs.logMobileError
import com.upcominds.expedite.data.user.UserRepository
import com.upcominds.expedite.model.CommunityLeader
import com.upcominds.expedite.model.Leader
import com.upcominds.expedite.model.User
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class LeadersUiState(
    val currentUserProfile: User? = null,
    val currentUser: Leader? = null,
    val global: List<Leader> = emptyList(),
    val globalLoading: Boolean = false,
    val globalLoadingMore: Boolean = false,
    val globalError: String? = null,
    val globalNextPage: Int = 0,
    val globalHasMore: Boolean = true,
    val communitiesLeaders: List<CommunityLeader> = emptyList(),
    val communitiesLoading: Boolean = false,
    val communitiesLoadingMore: Boolean = false,
    val communitiesError: String? = null,
    val activeCommunityContextId: String? = null,
    val communitiesNextPage: Int = 0,
    val communitiesHasMore: Boolean = true,
    val communityLeaders: List<Leader> = emptyList(),
    val currentCommunityId: String? = null,
    val communityLoading: Boolean = false,
    val communityLoadingMore: Boolean = false,
    val communityError: String? = null,
    val communityNextPage: Int = 0,
    val communityHasMore: Boolean = true,
)

class LeadersViewModel(
    private val userRepository: UserRepository,
    private val leadersRepository: LeadersRepository
): ViewModel() {

    private var activeCommunityLoadJob: Job? = null

    private val _uiState = MutableStateFlow(LeadersUiState())
    val uiState: StateFlow<LeadersUiState> = _uiState.asStateFlow()

    fun loadLeaders(activeCommunityId: String? = null) {
        ensureCurrentUserProfile()
        ensureCurrentUserLeader()

        val state = _uiState.value
        if (state.global.isEmpty()) {
            loadMoreGlobal(reset = true)
        }
        if (state.communitiesLeaders.isEmpty() || state.activeCommunityContextId != activeCommunityId) {
            loadActiveCommunitySummary(activeCommunityId)
        }
    }

    fun refresh() {
        ensureCurrentUserProfile()
        ensureCurrentUserLeader(force = true)
        refreshGlobal()
        refreshUserCommunities()
    }

    private fun loadActiveCommunitySummary(activeCommunityId: String?) {
        activeCommunityLoadJob?.cancel()
        _uiState.value = _uiState.value.copy(
            activeCommunityContextId = activeCommunityId,
            communitiesLoading = true,
            communitiesLoadingMore = false,
            communitiesError = null,
            communitiesLeaders = emptyList(),
            communitiesNextPage = 0,
            communitiesHasMore = true
        )
        activeCommunityLoadJob = viewModelScope.launch {
            try {
                val page = leadersRepository.getUserCommunitiesLeaders(0, PAGE_SIZE)
                if (_uiState.value.activeCommunityContextId != activeCommunityId) return@launch
                _uiState.value = _uiState.value.copy(
                    communitiesLoading = false,
                    communitiesLeaders = page,
                    communitiesNextPage = 1,
                    communitiesHasMore = page.size == PAGE_SIZE
                )
            } catch (e: Exception) {
                if (e is CancellationException) throw e
                logMobileError("LeadersViewModel.loadActiveCommunitySummary", e)
                if (_uiState.value.activeCommunityContextId == activeCommunityId) {
                    _uiState.value = _uiState.value.copy(
                        communitiesLoading = false,
                        communitiesError = e.message
                    )
                }
            }
        }
    }

    fun loadCommunityLeaders(communityId: String) {
        val state = _uiState.value
        if (state.currentCommunityId == communityId && (state.communityLeaders.isNotEmpty() || state.communityLoading || state.communityLoadingMore)) {
            return
        }
        loadMoreCommunityLeaders(communityId, reset = true)
    }

    fun loadMoreGlobal() {
        loadMoreGlobal(reset = false)
    }

    fun loadMoreUserCommunities() {
        loadMoreUserCommunities(reset = false)
    }

    fun loadMoreCommunityLeaders(communityId: String) {
        loadMoreCommunityLeaders(communityId, reset = false)
    }

    private fun ensureCurrentUserProfile() {
        if (_uiState.value.currentUserProfile != null) return

        viewModelScope.launch {
            try {
                val currentUser = userRepository.getCurrentUser()
                _uiState.value = _uiState.value.copy(
                    currentUserProfile = currentUser,
                    currentUser = _uiState.value.global.firstOrNull { it.id == currentUser.id } ?: _uiState.value.currentUser
                )
            } catch (e: Exception) {
                logMobileError("LeadersViewModel.ensureCurrentUserProfile", e)
                // The leaders screens can still function without profile data.
            }
        }
    }

    private fun ensureCurrentUserLeader(force: Boolean = false) {
        if (!force && _uiState.value.currentUser != null) return

        viewModelScope.launch {
            try {
                val leader = leadersRepository.getCurrentUserGlobalLeader()
                _uiState.value = _uiState.value.copy(
                    currentUser = leader ?: _uiState.value.currentUser
                )
            } catch (e: Exception) {
                logMobileError("LeadersViewModel.ensureCurrentUserLeader", e)
            }
        }
    }

    private fun loadMoreGlobal(reset: Boolean) {
        val state = _uiState.value
        if (state.globalLoading || state.globalLoadingMore) return
        if (!reset && !state.globalHasMore) return

        val nextPage = if (reset) 0 else state.globalNextPage
        _uiState.value = state.copy(
            globalLoading = reset,
            globalLoadingMore = !reset,
            globalError = null,
            global = if (reset) emptyList() else state.global,
            globalNextPage = if (reset) 0 else state.globalNextPage,
            globalHasMore = if (reset) true else state.globalHasMore
        )

        viewModelScope.launch {
            try {
                val page = leadersRepository.getGlobal(nextPage, PAGE_SIZE)
                val merged = if (reset) page else mergeLeaders(_uiState.value.global, page)
                val currentUserId = _uiState.value.currentUserProfile?.id
                _uiState.value = _uiState.value.copy(
                    globalLoading = false,
                    globalLoadingMore = false,
                    global = merged,
                    globalNextPage = nextPage + 1,
                    globalHasMore = page.size == PAGE_SIZE,
                    currentUser = merged.firstOrNull { it.id == currentUserId } ?: _uiState.value.currentUser
                )
            } catch (e: Exception) {
                logMobileError("LeadersViewModel.loadMoreGlobal", e)
                _uiState.value = _uiState.value.copy(
                    globalLoading = false,
                    globalLoadingMore = false,
                    globalError = e.message,
                )
            }
        }
    }

    private fun refreshGlobal() {
        val state = _uiState.value
        if (state.globalLoading || state.globalLoadingMore) return

        _uiState.value = state.copy(
            globalLoading = true,
            globalLoadingMore = false,
            globalError = null,
            globalNextPage = 0,
            globalHasMore = true
        )

        viewModelScope.launch {
            try {
                val page = leadersRepository.getGlobal(0, PAGE_SIZE)
                val currentUserId = _uiState.value.currentUserProfile?.id
                _uiState.value = _uiState.value.copy(
                    globalLoading = false,
                    globalLoadingMore = false,
                    global = page,
                    globalNextPage = 1,
                    globalHasMore = page.size == PAGE_SIZE,
                    currentUser = page.firstOrNull { it.id == currentUserId } ?: _uiState.value.currentUser
                )
            } catch (e: Exception) {
                logMobileError("LeadersViewModel.refreshGlobal", e)
                _uiState.value = _uiState.value.copy(
                    globalLoading = false,
                    globalLoadingMore = false,
                    globalError = e.message,
                )
            }
        }
    }

    private fun loadMoreUserCommunities(reset: Boolean) {
        val state = _uiState.value
        if (state.communitiesLoading || state.communitiesLoadingMore) return
        if (!reset && !state.communitiesHasMore) return

        val nextPage = if (reset) 0 else state.communitiesNextPage
        _uiState.value = state.copy(
            communitiesLoading = reset,
            communitiesLoadingMore = !reset,
            communitiesError = null,
            communitiesLeaders = if (reset) emptyList() else state.communitiesLeaders,
            communitiesNextPage = if (reset) 0 else state.communitiesNextPage,
            communitiesHasMore = if (reset) true else state.communitiesHasMore
        )

        viewModelScope.launch {
            try {
                val page = leadersRepository.getUserCommunitiesLeaders(nextPage, PAGE_SIZE)
                _uiState.value = _uiState.value.copy(
                    communitiesLoading = false,
                    communitiesLoadingMore = false,
                    communitiesLeaders = if (reset) page else mergeCommunityLeaders(_uiState.value.communitiesLeaders, page),
                    communitiesNextPage = nextPage + 1,
                    communitiesHasMore = page.size == PAGE_SIZE,
                )
            } catch (e: Exception) {
                logMobileError("LeadersViewModel.loadMoreUserCommunities", e)
                _uiState.value = _uiState.value.copy(
                    communitiesLoading = false,
                    communitiesLoadingMore = false,
                    communitiesError = e.message,
                )
            }
        }
    }

    private fun refreshUserCommunities() {
        val state = _uiState.value
        if (state.communitiesLoading || state.communitiesLoadingMore) return

        _uiState.value = state.copy(
            communitiesLoading = true,
            communitiesLoadingMore = false,
            communitiesError = null,
            communitiesNextPage = 0,
            communitiesHasMore = true
        )

        viewModelScope.launch {
            try {
                val page = leadersRepository.getUserCommunitiesLeaders(0, PAGE_SIZE)
                _uiState.value = _uiState.value.copy(
                    communitiesLoading = false,
                    communitiesLoadingMore = false,
                    communitiesLeaders = page,
                    communitiesNextPage = 1,
                    communitiesHasMore = page.size == PAGE_SIZE,
                )
            } catch (e: Exception) {
                logMobileError("LeadersViewModel.refreshUserCommunities", e)
                _uiState.value = _uiState.value.copy(
                    communitiesLoading = false,
                    communitiesLoadingMore = false,
                    communitiesError = e.message,
                )
            }
        }
    }

    private fun loadMoreCommunityLeaders(communityId: String, reset: Boolean) {
        val state = _uiState.value
        if (state.communityLoading || state.communityLoadingMore) return
        if (!reset && (state.currentCommunityId != communityId || !state.communityHasMore)) return

        val nextPage = if (reset || state.currentCommunityId != communityId) 0 else state.communityNextPage
        _uiState.value = state.copy(
            currentCommunityId = communityId,
            communityLoading = reset,
            communityLoadingMore = !reset,
            communityError = null,
            communityLeaders = if (reset) emptyList() else state.communityLeaders,
            communityNextPage = if (reset) 0 else state.communityNextPage,
            communityHasMore = if (reset) true else state.communityHasMore
        )

        viewModelScope.launch {
            try {
                val page = leadersRepository.getCommunity(communityId, nextPage, PAGE_SIZE)
                _uiState.value = _uiState.value.copy(
                    communityLoading = false,
                    communityLoadingMore = false,
                    communityLeaders = if (reset) page else mergeLeaders(_uiState.value.communityLeaders, page),
                    communityNextPage = nextPage + 1,
                    communityHasMore = page.size == PAGE_SIZE,
                )
            } catch (e: Exception) {
                logMobileError("LeadersViewModel.loadMoreCommunityLeaders", e, "Failed to load leaders for community $communityId")
                _uiState.value = _uiState.value.copy(
                    communityLoading = false,
                    communityLoadingMore = false,
                    communityError = e.message,
                )
            }
        }
    }

    private fun mergeLeaders(existing: List<Leader>, incoming: List<Leader>): List<Leader> {
        if (existing.isEmpty()) return incoming

        val seenIds = existing.mapTo(mutableSetOf()) { it.id }
        val merged = existing.toMutableList()
        incoming.forEach { leader ->
            if (seenIds.add(leader.id)) {
                merged += leader
            }
        }
        return merged
    }

    private fun mergeCommunityLeaders(existing: List<CommunityLeader>, incoming: List<CommunityLeader>): List<CommunityLeader> {
        if (existing.isEmpty()) return incoming

        val seenIds = existing.mapTo(mutableSetOf()) { it.community.id }
        val merged = existing.toMutableList()
        incoming.forEach { communityLeader ->
            if (seenIds.add(communityLeader.community.id)) {
                merged += communityLeader
            }
        }
        return merged
    }

    companion object {
        const val PAGE_SIZE = 20
    }
}
